import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const UsageHistoryModal = ({ isOpen, onClose, consumable }) => {
  const [usageHistory, setUsageHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen && consumable) {
      loadUsageHistory();
    }
  }, [isOpen, consumable]);

  const loadUsageHistory = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        ?.from('consumable_usage')
        ?.select(`
          *,
          technicians(name),
          appointments(reference, customer_name)
        `)
        ?.eq('consumable_id', consumable?.id)
        ?.order('usage_date', { ascending: false })
        ?.limit(50);

      if (error) throw error;
      setUsageHistory(data || []);
    } catch (err) {
      console.error('Error loading usage history:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1020] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="History" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Historique d'utilisation</h3>
                <p className="text-sm text-muted-foreground">{consumable?.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : usageHistory?.length === 0 ? (
            <div className="text-center py-12">
              <Icon name="History" size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucun historique d'utilisation</p>
            </div>
          ) : (
            <div className="space-y-3">
              {usageHistory?.map((usage) => (
                <div
                  key={usage?.id}
                  className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Icon name="User" size={16} className="text-muted-foreground" />
                        <span className="font-medium text-foreground">
                          {usage?.technicians?.name || 'Technicien inconnu'}
                        </span>
                      </div>
                      {usage?.appointments && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon name="FileText" size={14} />
                          <span>Intervention: {usage?.appointments?.reference}</span>
                          {usage?.appointments?.customer_name && (
                            <span>- {usage?.appointments?.customer_name}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-foreground">
                        -{usage?.quantity_used}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(usage?.usage_date)?.toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                  {usage?.notes && (
                    <div className="text-sm text-muted-foreground mt-2 pl-6">
                      {usage?.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full"
          >
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UsageHistoryModal;